/**
 * Shared expert-review UI: AmountInput.tsx.
 *
 * Belongs here: reusable presentational piece.
 * Does not belong here: flow rules or API.
 */
import { useEffect, useRef, useState } from 'react'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import {
  formatNumberWithoutCurrency,
  roundOffAndFormatNumberWithCurrency
} from '../../../../../utils/utils'
import { useToast } from '../../../../../state-management/contexts/ToastContext'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import {
  CONFIRM_ACTION_DELAY_MS,
  getContentSlideDurationMs
} from '../transitions/contentTransitionTimings'

interface AmountInputProps {
  questionId: string
  label: string
  /** Seeded committed/draft value from parent; typing stays local until Confirm. */
  value: number | undefined
  onClear?: () => void
  minValue?: number
  maxValue?: number
  onConfirm: (value: number) => void
  confirmLabel?: string
  helperText?: string
  isConfirmDisabled?: boolean
  confirmButtonClassName?: string
  testLocator?: string
}

export function AmountInput({
  questionId,
  label,
  value,
  onClear,
  minValue = 0,
  maxValue = 999999999,
  onConfirm,
  confirmLabel = 'Confirm',
  helperText,
  isConfirmDisabled: isConfirmDisabledProp,
  confirmButtonClassName,
  testLocator
}: AmountInputProps) {
  const { showToast } = useToast()
  const [localValue, setLocalValue] = useState<number | undefined>(value)
  const [clearedLocally, setClearedLocally] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const hasFocusedRef = useRef(false)

  useEffect(() => {
    setLocalValue(value)
    setClearedLocally(false)
    setIsConfirming(false)
  }, [questionId, value])

  const isConfirmDisabled =
    isConfirmDisabledProp ??
    (localValue == null || typeof localValue !== 'number' || localValue < minValue || localValue > maxValue)

  useEffect(() => {
    hasFocusedRef.current = false
  }, [questionId])

  useEffect(() => {
    if (hasFocusedRef.current) return
    const delay = getContentSlideDurationMs()
    const timeoutId = window.setTimeout(() => {
      hasFocusedRef.current = true
      inputRef.current?.focus()
    }, delay)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [])

  const isEmpty = localValue == null || typeof localValue !== 'number'
  const displayValue = clearedLocally || isEmpty ? '' : String(formatNumberWithoutCurrency(localValue))

  const isBelowMin = typeof localValue === 'number' && localValue < minValue
  const isAboveMax = typeof localValue === 'number' && localValue > maxValue
  const minError =
    minValue != null && minValue > 0
      ? `Minimum amount is ${roundOffAndFormatNumberWithCurrency(minValue)}`
      : null
  const maxError =
    maxValue != null && maxValue < 999999999
      ? `Maximum amount is ${roundOffAndFormatNumberWithCurrency(maxValue)}`
      : null

  return (
    <div className="mt-6">
      <div className="rounded-[12px] border border-[#EBF2F2] bg-white px-4 pt-3 pb-3 focus-within:border-[#232323]">
        <label
          htmlFor={`${questionId}-amount`}
          className="block text-[13px] font-[400] text-[#6A6E6E] mb-[2px]"
          {...(testLocator ? { 'test-locator': testLocator } : {})}
        >
          {label}
        </label>
        <div className="flex items-baseline gap-1">
          <span className="text-[16px] font-[500] text-[#232323] shrink-0" aria-hidden>
            ₹
          </span>
          <input
            id={`${questionId}-amount`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            ref={inputRef}
            placeholder=""
            value={displayValue}
            {...(testLocator ? { 'test-locator': `${testLocator}-input` } : {})}
            onChange={(event) => {
              const raw = event.target.value.replace(/\D/g, '')
              if (raw === '') {
                setClearedLocally(true)
                setLocalValue(undefined)
                onClear?.()
                return
              }
              setClearedLocally(false)
              const num = parseInt(raw, 10)
              if (!Number.isNaN(num)) setLocalValue(num)
            }}
            className="flex-1 min-w-0 border-none p-0 text-[16px] font-[500] text-[#232323] 
            placeholder:text-[#6A6E6E] focus:outline-none focus:ring-0 bg-transparent"
          />
        </div>
        {isBelowMin && minError ? (
          <p className="mt-2 text-[13px] font-[400] text-[#E77464]" {...(testLocator ? { 'test-locator': `${testLocator}-error-min` } : {})}>{minError}</p>
        ) : null}
        {isAboveMax && maxError ? (
          <p className="mt-1 text-[13px] font-[400] text-[#E77464]" {...(testLocator ? { 'test-locator': `${testLocator}-error-max` } : {})}>{maxError}</p>
        ) : null}
      </div>
      {helperText ? (
        <p className="mt-2 text-[13px] font-[400] text-[#8E9393] leading-[150%] px-2">{helperText}</p>
      ) : null}
      <div className="mt-6 relative">
        <PrimaryButton
          testLocator={testLocator ? `${testLocator}-confirm` : undefined}
          label={confirmLabel}
          onClick={() => {
            if (isConfirmDisabled || isConfirming || localValue == null) return
            setIsConfirming(true)
            window.setTimeout(() => {
              onConfirm(localValue)
            }, CONFIRM_ACTION_DELAY_MS)
          }}
          primaryButtonClassName={confirmButtonClassName ?? 'w-full !h-[56px] !font-[500] !rounded-[16px]'}
          isDisabled={isConfirmDisabled}
        >
          {isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <RenderHTML innerHTMLText={CONFIRMED_ICON_SVG} />
            </span>
          ) : null}
        </PrimaryButton>
        {isConfirmDisabled && (
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={() => showToast({ message: 'Enter amount to continue', width: '250px' })}
            aria-hidden
          />
        )}
      </div>
    </div>
  )
}

export const CONFIRMED_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M2.5 9.66667L5.89506 13L13.5 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
